import { Layout } from "@/components/Layout";
import { SeoHead } from "@/components/SeoHead";
import { JsonLd, faqPageSchema } from "@/components/JsonLd";
import { Button } from "@/components/ui/button";
import { Phone } from "lucide-react";
import { PHONE_HREF, PHONE_NUMBER } from "@/components/EmergencyBanner";

const faqItems = [
  { q: "How long do well pumps typically last?", a: "Most well pumps last 8 to 15 years depending on water quality, usage, and maintenance. Pumps in the Pocono Mountains may have shorter lifespans due to acidic water and high iron content common in the region. Regular maintenance and proper filtration help extend pump life." },
  { q: "Why does my well pump run constantly?", a: "A constantly running pump usually indicates a faulty pressure switch, a leak in the plumbing system, a waterlogged pressure tank, or a failing check valve. Each of these issues forces the pump to work continuously, which can burn out the motor if left unaddressed." },
  { q: "What causes low water pressure from my well?", a: "Low water pressure is typically caused by a failing pump, a clogged sediment filter, a waterlogged pressure tank, a partially closed valve, or a drop in the well's water table. A diagnostic test identifies the exact cause before any repair." },
  { q: "How quickly can you respond to emergencies?", a: "No-water emergency calls are prioritized and typically receive same-day response throughout the Pocono Mountains service area. Grinder pump emergencies are handled with the same urgency." },
  { q: "How much does well pump repair cost?", a: "Repair costs depend on the specific problem. Pressure switch replacement is the lowest-cost repair, while pump motor replacement costs more but is generally less than a full system installation. A diagnostic and clear price are provided before any work begins." },
  { q: "Should I repair or replace my well pump?", a: "If a pump is under 8 years old with a specific fixable problem, repair is usually the better option. Pumps over 10 to 12 years old experiencing multiple issues are often more cost-effective to replace." },
  { q: "What should I do if my well water smells like rotten eggs?", a: "A rotten egg smell indicates hydrogen sulfide gas in the water, which is common in Pocono Mountain wells. A properly sized water treatment system eliminates the odor and improves overall water quality." },
  { q: "Do you service all types of pumps?", a: "Yes. Service is provided for submersible pumps, jet pumps, constant pressure systems, residential and commercial grinder pumps, sewage pumps, and booster pumps across all major brands." },
  { q: "What areas do you serve?", a: "Service covers the Pocono Mountains region including Monroe County, Pike County, Carbon County, and Northampton County. Towns served include Stroudsburg, East Stroudsburg, Mount Pocono, Tobyhanna, Brodheadsville, Effort, Blakeslee, Tannersville, Bushkill, Lehighton, Wind Gap, and Bangor." },
  { q: "Are you licensed and insured?", a: "Yes. Semper Fi Pump Service is fully licensed and insured, and operates as a veteran-owned business." },
];

const FAQ = () => (
  <Layout>
    <SeoHead
      title="Well Pump FAQ | Semper Fi Pump Service"
      description="Answers to common well pump questions — lifespan, costs, low pressure, emergencies, and more. From your local Pocono Mountains pump experts."
    />
    <JsonLd data={faqPageSchema(faqItems)} />

    <section className="bg-primary">
      <div className="container py-16 md:py-24">
        <h1 className="text-3xl md:text-4xl font-black text-primary-foreground mb-4 border-l-0 pl-0">Frequently Asked Questions</h1>
        <p className="text-primary-foreground/80 max-w-2xl text-pretty">Answers to common questions about well pumps, water systems, and our services in the Pocono Mountains.</p>
      </div>
    </section>

    <section className="container py-16 md:py-24 max-w-3xl">
      <div className="space-y-6">
        {faqItems.map((item, i) => (
          <div key={i} className="bg-card rounded-sm p-6 shadow-card">
            <h2 className="font-bold text-foreground mb-3">{item.q}</h2>
            <p className="text-sm text-muted-foreground text-pretty leading-relaxed">{item.a}</p>
          </div>
        ))}
      </div>
    </section>

    <section className="bg-primary">
      <div className="container py-16 text-center">
        <h2 className="text-2xl font-black text-primary-foreground mb-4 border-l-0 pl-0 inline-block">Still Have Questions?</h2>
        <p className="text-primary-foreground/80 mb-8">Call us. We're happy to talk through your situation.</p>
        <Button asChild variant="emergency" size="xl">
          <a href={PHONE_HREF}><Phone className="w-5 h-5" />Call {PHONE_NUMBER}</a>
        </Button>
      </div>
    </section>
  </Layout>
);

export default FAQ;
